// src/utils/db.ts
const DB_NAME = 'MyProductDB';
const DB_VERSION = 14; // поднимай при изменении схемы (сторы/индексы)

export const STORE_NAMES = {
  coils: 'ignitionCoils',
  sparkPlugs: 'sparkPlugs',
  wires: 'wires',
  sensors: 'sensors',
  steeringClocks: 'steeringClocks',
  notifications: 'notifications',
  questions: 'questions',
  inbounds: 'inbounds',     // ← добавили
  expenses: 'expenses',     // ← если тратишь где-то — тоже сразу добавь
} as const;

export type StoreName = (typeof STORE_NAMES)[keyof typeof STORE_NAMES];

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      const txn = request.transaction!;

      // Создать отсутствующие сторы
      Object.values(STORE_NAMES).forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      });

      // Хелпер: взять стор из транзакции или создать
      const ensureStore = (name: StoreName) =>
        db.objectStoreNames.contains(name)
          ? txn.objectStore(name)
          : db.createObjectStore(name, { keyPath: 'id' });

      // Индексы для questions
      const q = ensureStore(STORE_NAMES.questions);
      if (!q.indexNames.contains('createdAt')) q.createIndex('createdAt', 'createdAt', { unique: false });
      if (!q.indexNames.contains('isRead')) q.createIndex('isRead', 'isRead', { unique: false });

      // Индексы для notifications
      const n = ensureStore(STORE_NAMES.notifications);
      if (!n.indexNames.contains('createdAt')) n.createIndex('createdAt', 'createdAt', { unique: false });
      if (!n.indexNames.contains('status')) n.createIndex('status', 'status', { unique: false });
    
          // Индексы для inbounds
      const ib = ensureStore(STORE_NAMES.inbounds);
      if (!ib.indexNames.contains('createdAt')) ib.createIndex('createdAt', 'createdAt', { unique: false });
      if (!ib.indexNames.contains('supplier'))  ib.createIndex('supplier',  'supplier',  { unique: false });

      // Индексы для expenses
      const ex = ensureStore(STORE_NAMES.expenses);
      if (!ex.indexNames.contains('createdAt')) ex.createIndex('createdAt', 'createdAt', { unique: false });

    };

    request.onblocked = () => {
      console.warn('IndexedDB upgrade is blocked by another open tab.');
      // важное: не висим, а сразу даём ошибку, чтобы хук сделал фолбэк
      reject(new Error('IDB upgrade blocked'));
    };

    request.onsuccess = () => {
      const db = request.result;
      // Если другая вкладка инициирует обновление — закрываемся
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onerror = () => reject(request.error);
  });
}

const db = {
  async getAll<T>(storeName: StoreName): Promise<T[]> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (getAll)'));
      tx.oncomplete = () => {/* no-op: resolve уже вызван выше */};
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (getAll)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (getAll)'));
    });
  },

  async get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.get(key);
      req.onsuccess = () => resolve((req.result as T) ?? undefined);
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (get)'));
      tx.oncomplete = () => {/* ok */};
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (get)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (get)'));
    });
  },

  async add<T extends { id: IDBValidKey }>(storeName: StoreName, item: T): Promise<void> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.add(item);
      req.onsuccess = () => {/* дождёмся oncomplete */}
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (add)'));
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (add)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (add)'));
    });
  },

  async put<T>(storeName: StoreName, item: T): Promise<void> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.put(item as any);
      req.onsuccess = () => {/* дождёмся oncomplete */}
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (put)'));
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (put)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (put)'));
    });
  },

  async delete(storeName: StoreName, key: IDBValidKey): Promise<void> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.delete(key);
      req.onsuccess = () => {/* дождёмся oncomplete */}
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (delete)'));
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (delete)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (delete)'));
    });
  },

  async clear(storeName: StoreName): Promise<void> {
    const database = await openDB();
    return new Promise((resolve, reject) => {
      const tx = database.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.clear();
      req.onsuccess = () => {/* дождёмся oncomplete */}
      req.onerror   = () => reject(req.error ?? new Error('IDB request error (clear)'));
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error ?? new Error('IDB tx error (clear)'));
      tx.onabort    = () => reject(tx.error ?? new Error('IDB tx abort (clear)'));
    });
  },
};

export default db;
export { openDB,  DB_NAME, DB_VERSION };
// Совместимость со старыми импортами:
export { db as dbAPI };
